import { RotateCcw } from 'lucide-react';
import { Panel, VStack, HStack, Divider } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { TextField, Select, Checkbox } from '@components/base/inputs';
import type { PropDefinition } from '../../showcases/types';

interface PropsPlaygroundProps {
  propsDefinition: PropDefinition[];
  values: Record<string, unknown>;
  onChange: (newValues: Record<string, unknown>) => void;
  onReset: () => void;
}

export function PropsPlayground({
  propsDefinition,
  values,
  onChange,
  onReset,
}: Readonly<PropsPlaygroundProps>) {
  const handleChange = (name: string, value: unknown) => {
    onChange({ ...values, [name]: value });
  };

  if (propsDefinition.length === 0) {
    return null;
  }

  return (
    <Panel variant="outlined">
      <VStack gap={4}>
        <HStack justify="between" align="center">
          <Text as="h3" size="base" weight="semibold">
            Props Playground
          </Text>
          <Button variant="ghost" size="sm" onClick={onReset}>
            <Icon icon={RotateCcw} size="sm" className="mr-1" />
            Reset
          </Button>
        </HStack>
        <Divider spacing={0} color="light" />
        <VStack gap={4}>
          {propsDefinition.map((prop) => (
            <VStack key={prop.name} gap={1}>
              {prop.type === 'boolean' && (
                <Checkbox
                  label={prop.name}
                  checked={values[prop.name] as boolean}
                  onChange={(e) => {
                    handleChange(prop.name, e.target.checked);
                  }}
                />
              )}
              {prop.type === 'select' && prop.options && (
                <Select
                  label={prop.name}
                  value={values[prop.name] as string}
                  onChange={(value) => {
                    handleChange(prop.name, value);
                  }}
                  options={prop.options.map((opt) => ({
                    value: opt,
                    label: opt,
                  }))}
                />
              )}
              {prop.type === 'string' && (
                <TextField
                  label={prop.name}
                  value={values[prop.name] as string}
                  onChange={(e) => {
                    handleChange(prop.name, e.target.value);
                  }}
                />
              )}
              {prop.type === 'number' && (
                <TextField
                  label={prop.name}
                  type="number"
                  value={String(values[prop.name])}
                  onChange={(e) => {
                    handleChange(prop.name, Number(e.target.value));
                  }}
                />
              )}
              <Text size="xs" color="muted">
                {prop.description}
              </Text>
            </VStack>
          ))}
        </VStack>
      </VStack>
    </Panel>
  );
}
